"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckIcon, XIcon } from "@phosphor-icons/react";
import { DateTime } from "luxon";
import { useState } from "react";
import { formatDateTimeDisplay } from "@/packages/shared/src/utils/date-utils";
import { SchedulingEligibility } from "@/src/components/comms/scheduling-eligibility";
import { Button } from "@/src/components/shadcn/ui/button";
import { Checkbox } from "@/src/components/shadcn/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import { Textarea } from "@/src/components/shadcn/ui/textarea";
import { useUser } from "@/src/context/user-context";
import { useReferralSchedulingRecommendation, useVerifySchedulingRecommendation } from "@/src/lib/data-fetchers/scheduling-fetchers";
import { cn } from "@/src/lib/shadcn-utils";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { Referral } from "@/src/models/documents/referral";
import { SCHEDULING_VERIFICATION_REASON_CODES, SCHEDULING_VERIFICATION_REASON_LABELS, SchedulingRecommendation, SchedulingVerificationReasonCode, VerificationReason, shouldRecommendForSchedulingInReferralTask } from "@/src/models/scheduling-recommendation";
interface ReferralSchedulingRecommendationProps {
  referral: Referral;
  title?: string;
}
export function ReferralSchedulingRecommendation(t0) {
  const $ = _c(19);
  const {
    referral,
    title: t1
  } = t0;
  const title = t1 === undefined ? "Scheduling recommendation" : t1;
  const {
    group
  } = useUser();
  const {
    recommendation: referralSchedulingRecommendation,
    isPending: isReferralSchedulingRecommendationPending,
    isError
  } = useReferralSchedulingRecommendation(referral?.id, true);
  let t2;
  if ($[0] !== group || $[1] !== referralSchedulingRecommendation) {
    t2 = shouldRecommendForSchedulingInReferralTask(group, referralSchedulingRecommendation);
    $[0] = group;
    $[1] = referralSchedulingRecommendation;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const shouldRecommendForSchedulingInReferralTaskBoolean = t2;
  if (!referral?.id) {
    let t3;
    if ($[3] !== title) {
      t3 = title ? <h3 className="font-semibold text-foreground my-2">{title}</h3> : null;
      $[3] = title;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <span className="text-sm text-muted-foreground">No referral ID available</span>;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t3) {
      t5 = <div className="mb-4 my-2">{t3}{t4}</div>;
      $[6] = t3;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  let t3;
  if ($[8] !== title) {
    t3 = title ? <h3 className="font-semibold text-foreground my-2">{title}</h3> : null;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== isError || $[11] !== isReferralSchedulingRecommendationPending || $[12] !== referral || $[13] !== referralSchedulingRecommendation || $[14] !== shouldRecommendForSchedulingInReferralTaskBoolean) {
    t4 = isReferralSchedulingRecommendationPending ? <span className="text-sm text-muted-foreground">Checking for eligibility...</span> : isError ? <span className="text-sm text-muted-foreground">Unable to load recommendation</span> : <div className="flex flex-col gap-2"><div className="flex items-center gap-2"><span className="text-sm font-medium text-foreground">{shouldRecommendForSchedulingInReferralTaskBoolean ? "Recommended" : "Not Recommended"}</span><SchedulingEligibility agenticRecommendation={referralSchedulingRecommendation?.agenticRecommendation} receivingProvider={referral.receivingProvider} /></div><RecommendedSlotSection recommendation={referralSchedulingRecommendation} /><SchedulingRecommendationVerification referralId={referral.id} recommendation={referralSchedulingRecommendation} /></div>;
    $[10] = isError;
    $[11] = isReferralSchedulingRecommendationPending;
    $[12] = referral;
    $[13] = referralSchedulingRecommendation;
    $[14] = shouldRecommendForSchedulingInReferralTaskBoolean;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t3 || $[17] !== t4) {
    t5 = <div className="mb-4 my-2">{t3}{t4}</div>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}
interface RecommendedSlotSectionProps {
  recommendation: SchedulingRecommendation | null;
}
export function RecommendedSlotSection(t0) {
  const $ = _c(4);
  const {
    recommendation
  } = t0;
  if (recommendation?.recommendedAppointment) {
    let t1;
    if ($[0] !== recommendation.recommendedAppointment) {
      t1 = <RecommendedSlotSummary appointment={recommendation.recommendedAppointment} />;
      $[0] = recommendation.recommendedAppointment;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (recommendation?.noRecommendationReason) {
    let t1;
    if ($[2] !== recommendation.noRecommendationReason.explanation) {
      t1 = <span className="text-sm text-muted-foreground">No appointment recommended: {recommendation.noRecommendationReason.explanation}</span>;
      $[2] = recommendation.noRecommendationReason.explanation;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  return null;
}
interface RecommendedSlotSummaryProps {
  appointment: NonNullable<SchedulingRecommendation["recommendedAppointment"]>;
}
function RecommendedSlotSummary(t0) {
  const $ = _c(21);
  const {
    appointment
  } = t0;
  const {
    providerName,
    locationName,
    reasoning,
    appointmentSlot
  } = appointment;
  let t1;
  if ($[0] !== appointmentSlot.end) {
    t1 = DateTime.fromISO(appointmentSlot.end, {
      setZone: true
    }).toFormat("h:mm a");
    $[0] = appointmentSlot.end;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const endTime = t1;
  let t2;
  if ($[2] !== appointmentSlot.start) {
    t2 = formatDateTimeDisplay(appointmentSlot.start);
    $[2] = appointmentSlot.start;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const formattedRange = `${t2} – ${endTime}`;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-sm font-medium text-foreground">Recommended appointment</span>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = providerName ?? appointmentSlot.practitionerDisplayName ?? "Unknown provider";
  const t5 = locationName ?? appointmentSlot.locationName ?? "Unknown location";
  let t6;
  if ($[5] !== t4 || $[6] !== t5) {
    t6 = <span className="text-sm text-foreground">{t4}{" at "}{t5}</span>;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== formattedRange) {
    t7 = <span className="text-sm text-muted-foreground">{formattedRange}</span>;
    $[8] = formattedRange;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== appointmentSlot.appointmentType) {
    t8 = formatFromUnderscoreToHumanReadable(appointmentSlot.appointmentType);
    $[10] = appointmentSlot.appointmentType;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t8) {
    t9 = <span className="text-sm text-muted-foreground">{t8}</span>;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== reasoning) {
    t10 = reasoning ? <span className="text-xs text-muted-foreground">{reasoning}</span> : null;
    $[14] = reasoning;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t6 || $[18] !== t7 || $[19] !== t9) {
    t11 = <div className="flex flex-col gap-1 rounded-md border border-border bg-muted/40 p-2">{t3}{t6}{t7}{t9}{t10}</div>;
    $[16] = t10;
    $[17] = t6;
    $[18] = t7;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
interface SchedulingRecommendationVerificationProps {
  referralId: string | null | undefined;
  recommendation: SchedulingRecommendation | null;
}

// Correct/Incorrect verification controls for a referral scheduling recommendation. Operates on an
// already-fetched recommendation so callers that have loaded it (e.g. the ENTP referral message)
// can reuse the buttons without triggering a second recommendation fetch.
export function SchedulingRecommendationVerification(t0) {
  const $ = _c(13);
  const {
    referralId,
    recommendation
  } = t0;
  const verifyMutation = useVerifySchedulingRecommendation(referralId);
  const isCorrect = recommendation?.isCorrect ?? null;
  const verificationReason = recommendation?.verificationReason ?? null;
  if (!recommendation) {
    return null;
  }
  let t1;
  if ($[0] !== isCorrect || $[1] !== verifyMutation) {
    t1 = () => verifyMutation.mutate({
      isCorrect: isCorrect === true ? null : true,
      verificationReason: null
    });
    $[0] = isCorrect;
    $[1] = verifyMutation;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  let t3;
  if ($[3] !== verifyMutation) {
    t2 = reason => verifyMutation.mutate({
      isCorrect: false,
      verificationReason: reason
    });
    t3 = () => verifyMutation.mutate({
      isCorrect: null,
      verificationReason: null
    });
    $[3] = verifyMutation;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isCorrect || $[7] !== t1 || $[8] !== t2 || $[9] !== t3 || $[10] !== verificationReason || $[11] !== verifyMutation.isPending) {
    t4 = <SchedulingVerificationControls isCorrect={isCorrect} verificationReason={verificationReason} isPending={verifyMutation.isPending} onMarkCorrect={t1} onMarkIncorrect={t2} onClear={t3} />;
    $[6] = isCorrect;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = verificationReason;
    $[11] = verifyMutation.isPending;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
interface SchedulingVerificationControlsProps {
  isCorrect: boolean | null;
  verificationReason: VerificationReason | null;
  isPending: boolean;
  onMarkCorrect: () => void;
  onMarkIncorrect: (reason: VerificationReason) => void;
  // Omit when clearing an existing verification isn't meaningful (e.g. the verification task, which
  // completes on the first choice); the Clear affordance is then hidden.
  onClear?: () => void;
}

// Presentational Correct/Incorrect controls, decoupled from how the verification is persisted. Both
// the inline referral verification and the standalone Scheduling Eligibility Verification task reuse
// this; callers supply the mutation behind the callbacks.
export function SchedulingVerificationControls(t0) {
  const $ = _c(24);
  const {
    isCorrect,
    verificationReason,
    isPending,
    onMarkCorrect,
    onMarkIncorrect,
    onClear
  } = t0;
  const t1 = isCorrect === true ? "default" : "outline";
  const t2 = isCorrect === true;
  const t3 = isCorrect === true && "bg-success hover:bg-success/90";
  let t4;
  if ($[0] !== t3) {
    t4 = cn("h-7 gap-1 px-2", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CheckIcon className="h-3.5 w-3.5" weight="bold" />;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== isPending || $[4] !== onMarkCorrect || $[5] !== t1 || $[6] !== t2 || $[7] !== t4) {
    t6 = <Button type="button" size="sm" variant={t1} disabled={isPending} onClick={onMarkCorrect} aria-pressed={t2} className={t4}>{t5}Correct</Button>;
    $[3] = isPending;
    $[4] = onMarkCorrect;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = isCorrect === false;
  let t8;
  if ($[9] !== isPending || $[10] !== onClear || $[11] !== onMarkIncorrect || $[12] !== t7 || $[13] !== verificationReason) {
    t8 = <IncorrectReasonButton isActive={t7} initialReason={verificationReason} isPending={isPending} onSubmit={onMarkIncorrect} onClear={onClear} />;
    $[9] = isPending;
    $[10] = onClear;
    $[11] = onMarkIncorrect;
    $[12] = t7;
    $[13] = verificationReason;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6 || $[16] !== t8) {
    t9 = <div className="flex items-center gap-1">{t6}{t8}</div>;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== isCorrect || $[19] !== verificationReason) {
    t10 = isCorrect === false && verificationReason ? <span className="text-xs text-muted-foreground">{verificationReason.reasons.map(_temp).join(", ")}{verificationReason.other ? `: ${verificationReason.other}` : ""}</span> : null;
    $[18] = isCorrect;
    $[19] = verificationReason;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = <div className="flex flex-col gap-1">{t9}{t10}</div>;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}
function _temp(code) {
  return SCHEDULING_VERIFICATION_REASON_LABELS[code];
}
interface IncorrectReasonButtonProps {
  isActive: boolean;
  initialReason: VerificationReason | null;
  isPending: boolean;
  onSubmit: (reason: VerificationReason) => void;
  onClear?: () => void;
}

// Marking a recommendation incorrect requires at least one reason, so the choice lives in a
// popover that only commits on Save (unlike the instant Correct toggle).
function IncorrectReasonButton(t0) {
  const $ = _c(47);
  const {
    isActive,
    initialReason,
    isPending,
    onSubmit,
    onClear
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [reasons, setReasons] = useState(t1);
  const [other, setOther] = useState("");
  let t2;
  if ($[1] !== initialReason?.other || $[2] !== initialReason?.reasons) {
    t2 = next => {
      if (next) {
        setReasons(initialReason?.reasons ?? []);
        setOther(initialReason?.other ?? "");
      }
      setOpen(next);
    };
    $[1] = initialReason?.other;
    $[2] = initialReason?.reasons;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleOpenChange = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = code => {
      setReasons(prev => prev.includes(code) ? prev.filter(value => value !== code) : [...prev, code]);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const toggleReason = t3;
  let t4;
  if ($[5] !== reasons) {
    t4 = reasons.includes("other");
    $[5] = reasons;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const otherSelected = t4;
  let t5;
  if ($[7] !== other || $[8] !== otherSelected || $[9] !== reasons.length) {
    t5 = reasons.length > 0 && (!otherSelected || other.trim().length > 0);
    $[7] = other;
    $[8] = otherSelected;
    $[9] = reasons.length;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const canSave = t5;
  let t6;
  if ($[11] !== onSubmit || $[12] !== other || $[13] !== otherSelected || $[14] !== reasons) {
    t6 = () => {
      onSubmit({
        reasons,
        other: otherSelected ? other.trim() : null
      });
      setOpen(false);
    };
    $[11] = onSubmit;
    $[12] = other;
    $[13] = otherSelected;
    $[14] = reasons;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleSave = t6;
  const t7 = isActive ? "destructive" : "outline";
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <XIcon className="h-3.5 w-3.5" weight="bold" />;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isActive || $[18] !== isPending || $[19] !== t7) {
    t9 = <PopoverTrigger asChild={true}><Button type="button" size="sm" variant={t7} disabled={isPending} aria-pressed={isActive} className="h-7 gap-1 px-2">{t8}Incorrect</Button></PopoverTrigger>;
    $[17] = isActive;
    $[18] = isPending;
    $[19] = t7;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <p className="text-sm font-medium text-foreground">Why is this incorrect?</p>;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== reasons) {
    t11 = SCHEDULING_VERIFICATION_REASON_CODES.map(code_0 => <label key={code_0} className="flex items-center gap-2 text-sm text-foreground"><Checkbox checked={reasons.includes(code_0)} onCheckedChange={() => toggleReason(code_0)} />{SCHEDULING_VERIFICATION_REASON_LABELS[code_0]}</label>);
    $[22] = reasons;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <div className="flex flex-col gap-1.5">{t11}</div>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== other || $[27] !== otherSelected) {
    t13 = otherSelected ? <Textarea value={other} onChange={event => setOther(event.target.value)} placeholder="Describe the issue" /> : null;
    $[26] = other;
    $[27] = otherSelected;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== isActive || $[30] !== onClear) {
    t14 = isActive && onClear ? <Button type="button" size="sm" variant="ghost" onClick={() => {
      onClear();
      setOpen(false);
    }} className="mr-auto h-7 px-2">Clear</Button> : null;
    $[29] = isActive;
    $[30] = onClear;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = !canSave || isPending;
  let t16;
  if ($[32] !== handleSave || $[33] !== t15) {
    t16 = <Button type="button" size="sm" disabled={t15} onClick={handleSave} className="h-7 px-2">Save</Button>;
    $[32] = handleSave;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t14 || $[36] !== t16) {
    t17 = <div className="flex items-center justify-end gap-2">{t14}{t16}</div>;
    $[35] = t14;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t12 || $[39] !== t13 || $[40] !== t17) {
    t18 = <PopoverContent className="w-64" align="start"><div className="flex flex-col gap-2">{t10}{t12}{t13}{t17}</div></PopoverContent>;
    $[38] = t12;
    $[39] = t13;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== handleOpenChange || $[43] !== open || $[44] !== t18 || $[45] !== t9) {
    t19 = <Popover open={open} onOpenChange={handleOpenChange}>{t9}{t18}</Popover>;
    $[42] = handleOpenChange;
    $[43] = open;
    $[44] = t18;
    $[45] = t9;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
}